import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Edit3, ExternalLink, Save, X } from "lucide-react";

const PLATFORM_META = {
  discord: { name: "Discord", accent: "#5865F2" },
  twitch: { name: "Twitch", accent: "#9146FF" },
  youtube: { name: "YouTube", accent: "#FF0033" },
  twitter: { name: "Twitter / X", accent: "#C0C0C0" },
  instagram: { name: "Instagram", accent: "#E1306C" },
};

export default function LinksPage() {
  const { user } = useAuth();
  const { t } = useSettings();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/links");
        setLinks(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const startEdit = () => { setDraft(links.map((l) => ({ ...l }))); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft([]); };
  const save = async () => {
    try {
      const { data } = await api.put("/links", { links: draft });
      setLinks(data);
      setEditing(false);
      toast.success("Links updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  return (
    <div className="px-4 sm:px-8 py-10 sm:py-16 max-w-5xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-[10px] tracking-[0.5em] uppercase text-crimson-bright mb-3">{t("links.eyebrow")}</div>
          <h1 className="font-display font-black tracking-[0.08em] uppercase text-3xl sm:text-5xl text-white">
            {t("links.title")} <span className="text-crimson">{t("links.accent")}</span>
          </h1>
          <p className="text-zinc-400 mt-3 text-sm max-w-xl">{t("links.subtitle")}</p>
        </div>
        {user?.role === "admin" && !editing && (
          <button data-testid="edit-links" onClick={startEdit} className="btn-ghost px-4 py-2 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2">
            <Edit3 size={14} /> Edit
          </button>
        )}
        {editing && (
          <div className="flex gap-2">
            <button data-testid="save-links" onClick={save} className="btn-crimson px-4 py-2 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2">
              <Save size={14} /> Save
            </button>
            <button onClick={cancelEdit} className="btn-ghost px-4 py-2 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2">
              <X size={14} /> Cancel
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-silver text-sm tracking-widest uppercase animate-pulse">Loading...</div>
      ) : editing ? (
        <div className="space-y-3" data-testid="links-editor">
          {draft.map((l, i) => (
            <div key={i} className="tavern-card p-4 grid sm:grid-cols-[160px_1fr] gap-3">
              <input
                className="tavern-input px-3 py-2 rounded-sm"
                value={l.label || PLATFORM_META[l.platform]?.name || l.platform}
                onChange={(e) => setDraft((d) => d.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))}
                data-testid={`link-label-${l.platform}`}
              />
              <input
                className="tavern-input px-3 py-2 rounded-sm"
                value={l.url}
                onChange={(e) => setDraft((d) => d.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x))}
                placeholder="https://..."
                data-testid={`link-url-${l.platform}`}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {links.map((l, i) => {
            const meta = PLATFORM_META[l.platform] || { name: l.label || l.platform, accent: "#880808" };
            return (
              <a
                key={l.platform + i}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`link-${l.platform}`}
                className="tavern-card group p-6 sm:p-7 flex items-center justify-between hover:border-crimson/60 transition-all hover:-translate-y-0.5 animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className="w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center font-display font-bold text-base"
                    style={{ backgroundColor: `${meta.accent}20`, border: `1px solid ${meta.accent}60`, color: meta.accent }}
                  >
                    {meta.name[0]}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display tracking-[0.2em] uppercase text-white font-semibold truncate">{l.label || meta.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{l.url.replace(/^https?:\/\//, "")}</div>
                  </div>
                </div>
                <ExternalLink size={18} className="text-zinc-600 group-hover:text-crimson-bright transition-colors flex-shrink-0" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
